import React, { useState } from "react";
import { OtAttempt2, OtPosition } from "./PositionStats";
import { ActionIcon, Pagination, SegmentedControl, Select, Table, Text, Title, Tooltip } from "@mantine/core";
import dayjs from "dayjs";
import { IconDownload, IconLink } from "@tabler/icons-react";
import { analyzeAttemptOnLichces, EQUALIBRIUM } from "./utils";
import { Chessboard } from "react-chessboard";
import { Chess } from "chess.js";
import { usePersistedStorage } from "./ZustandState";
import { useMediaQuery } from "@mantine/hooks";

interface Props {
    attempts: OtAttempt2[];
    opening: OtPosition;
}

type RecentGamesTab = "all" | "gains" | "losses";

const RecentGamesTable: React.FC<Props> = ({ attempts, opening }) => {
    const [recentGamesTab, setRecentGamesTab] = useState<RecentGamesTab>("all");
    const [page, setPage] = useState(1);
    const rowsPerPage = usePersistedStorage((state) => state.rowsPerPage);
    const setRowsPerPage = usePersistedStorage((state) => state.setRowsPerPage);
    const isMobile = useMediaQuery("(max-width: 600px)");
    const isMobile2 = useMediaQuery("(max-width: 1024px)");

    const successAttempts = attempts.filter((attempt) =>
        attempt.playerColor === "w"
            ? parseFloat(attempt.evaluation) >= EQUALIBRIUM
            : parseFloat(attempt.evaluation) <= EQUALIBRIUM
    );

    const mistakeAttempts = attempts.filter((attempt) =>
        attempt.playerColor === "w"
            ? parseFloat(attempt.evaluation) < EQUALIBRIUM
            : parseFloat(attempt.evaluation) > EQUALIBRIUM
    );

    let attemptsToDisplay: OtAttempt2[] = [];
    switch (recentGamesTab) {
        case "all":
            attemptsToDisplay = attempts;
            break;
        case "gains":
            attemptsToDisplay = successAttempts;
            break;
        case "losses":
            attemptsToDisplay = mistakeAttempts;
            break;
        default:
            break;
    }

    const handleOnDownload = () => {
        const fileName = `${opening.name}_${recentGamesTab}.pgn`;
        const element = document.createElement("a");
        const pgns = attemptsToDisplay.map((attempt) => attempt.pgn).join("\n\n");
        const file = new Blob([pgns], { type: "text/plain" });
        element.href = URL.createObjectURL(file);
        element.download = fileName;
        document.body.appendChild(element);
        element.click();
    };

    const handleSetRecentGamesTab = (tab: RecentGamesTab) => {
        setRecentGamesTab(tab);
        setPage(1);
    };

    return (
        <div>
            <div className="mt-24 space-y-4">
                <div className="flex items-center justify-center space-x-2">
                    <Title order={3}>Recent games</Title>
                    <ActionIcon onClick={handleOnDownload} aria-label="Download games" variant="transparent">
                        <IconDownload />
                    </ActionIcon>
                </div>
                <SegmentedControl
                    w="24rem"
                    className="mt-0.5"
                    styles={{
                        root: {
                            backgroundColor: "#1c1d21",
                        },
                    }}
                    color="blue"
                    value={recentGamesTab}
                    onChange={(tab) => handleSetRecentGamesTab(tab as RecentGamesTab)}
                    data={[
                        {
                            value: "all",
                            label: `All (${attempts.length})`,
                        },
                        {
                            value: "gains",
                            label: `Wins (${successAttempts.length})`,
                        },
                        {
                            value: "losses",
                            label: `Losses (${mistakeAttempts.length})`,
                        },
                    ]}
                />
            </div>
            <Table verticalSpacing="lg" className="text-left" highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>
                            <Text fw="bold">Date</Text>
                        </Table.Th>
                        <Table.Th>
                            <Text fw="bold">Rating</Text>
                        </Table.Th>
                        {!isMobile && (
                            <Table.Th>
                                <Text fw="bold">Opponent</Text>
                            </Table.Th>
                        )}
                        <Table.Th>
                            <Text fw="bold">{isMobile ? "Eval" : "Evaluation"}</Text>
                        </Table.Th>
                        <Table.Th>
                            <Text fw="bold">{isMobile ? "Result" : "Rating gain"}</Text>
                        </Table.Th>
                        <Table.Th>
                            <Text fw="bold">Link</Text>
                        </Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {attemptsToDisplay?.slice(page * rowsPerPage - rowsPerPage, page * rowsPerPage).map((element) => {
                        const chess = new Chess();
                        chess.loadPgn(element.pgn);
                        const position = chess.fen();
                        return (
                            <Tooltip
                                key={element.id}
                                position="right"
                                withArrow
                                arrowSize={16}
                                label={
                                    <div className="w-48">
                                        <Chessboard
                                            position={position}
                                            boardOrientation={element.playerColor === "w" ? "white" : "black"}
                                        />
                                    </div>
                                }
                            >
                                <Table.Tr key={element.id}>
                                    <Table.Td>
                                        <Text>{dayjs(element.createdAt).format("D. MMM")}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Text>{element.positionRating}</Text>
                                    </Table.Td>
                                    {!isMobile && (
                                        <Table.Td>
                                            <Text>
                                                {element.selectedEngineLevel === "Masters"
                                                    ? "Masters"
                                                    : element.engineLevel}
                                            </Text>
                                        </Table.Td>
                                    )}
                                    <Table.Td>
                                        <Text>{element.evaluation}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                        {element.isRated && (
                                            <Text c={parseInt(element.ratingGain) >= 0 ? "green" : "red"}>
                                                {parseInt(element.ratingGain) >= 0 ? "+" : ""}
                                                {element.ratingGain}
                                            </Text>
                                        )}
                                    </Table.Td>
                                    <Table.Td>
                                        <ActionIcon
                                            onClick={() => analyzeAttemptOnLichces(element, isMobile2)}
                                            aria-label="Open game"
                                            variant="transparent"
                                        >
                                            <IconLink />
                                        </ActionIcon>
                                    </Table.Td>
                                </Table.Tr>
                            </Tooltip>
                        );
                    })}
                </Table.Tbody>
            </Table>
            {attemptsToDisplay.length > 5 && (
                <div className="flex justify-between items-center mt-8 px-3 space-x-16">
                    {/* <Text c="dimmed">Total: {attempts.length}</Text> */}
                    <Select
                        value={rowsPerPage.toString()}
                        onChange={(value) => setRowsPerPage(parseInt(value as string))}
                        data={[
                            { value: "5", label: "5 per page" },
                            { value: "10", label: "10 per page" },
                            { value: "20", label: "20 per page" },
                            { value: "50", label: "50 per page" },
                        ]}
                        w="8rem"
                    />

                    <Pagination
                        value={page}
                        onChange={setPage}
                        total={Math.ceil(attemptsToDisplay.length / rowsPerPage)}
                    />
                </div>
            )}
        </div>
    );
};

export default RecentGamesTable;
